import { FieldType, ListFieldDefinition, ObjectType } from "../queries/getCrmListFieldDefinitions";
import {
  StringCondition,
  NumberCondition,
  BooleanCondition,
  DropdownCondition,
  DateCondition,
  StringConditionWithoutEmpty,
  NumberConditionWithoutEmpty,
  DateConditionWithoutEmpty,
  DropdownConditionWithoutEmpty,
} from "../queries/getCrmRequests";

export const FieldTypeOptions: FieldType[] = [
  "Address",
  "Checkbox",
  "Currency",
  "Date",
  "DateTime",
  "Dropdown",
  "DropdownMultiSelect",
  "Email",
  "Number",
  "Percent",
  "PhoneNumber",
  "Text",
  "TextArea",
  "Time",
  "URL",
  "LookUp",
];

export const StringConditionsWithoutEmpty: StringConditionWithoutEmpty[] = [
  "Equal",
  "NotEqual",
  "Contains",
  "NotContains",
  "StartsWith",
  "NotStartsWith",
];

export const StringConditions: StringCondition[] = [...StringConditionsWithoutEmpty, "Empty", "NotEmpty"];

export const NumberConditionsWithoutEmpty: NumberConditionWithoutEmpty[] = [
  "Equal",
  "NotEqual",
  "GreaterThan",
  "LessThan",
  "GreaterThanOrEqual",
  "LessThanOrEqual",
  "Between",
];
export const NumberConditions: NumberCondition[] = [...NumberConditionsWithoutEmpty, "Empty", "NotEmpty"];

export const DateConditionsWithoutEmpty: DateConditionWithoutEmpty[] = [
  "Equal",
  "NotEqual",
  "GreaterThan",
  "LessThan",
  "GreaterThanOrEqual",
  "LessThanOrEqual",
];
export const DateConditions: DateCondition[] = [...DateConditionsWithoutEmpty, "Empty", "NotEmpty"];

export const BooleanConditions: BooleanCondition[] = ["True", "False"];

export const DropdownConditionsWithoutEmpty: DropdownConditionWithoutEmpty[] = [
  "Any",
  "NotAny",
  "Contains",
  "NotContains",
  "StartsWith",
  "NotStartsWith",
];
export const DropdownConditions: DropdownCondition[] = [...DropdownConditionsWithoutEmpty, "Empty", "NotEmpty"];

export const ConditionTypeOptionsMap = {
  Text: StringConditions,
  TextArea: StringConditions,
  Email: StringConditions,
  URL: StringConditions,
  Address: StringConditions,
  PhoneNumber: StringConditions,
  Number: NumberConditions,
  Currency: NumberConditions,
  Percent: NumberConditions,
  Date: DateConditions,
  DateTime: DateConditions,
  Time: DateConditions,
  Checkbox: BooleanConditions,
  Dropdown: DropdownConditions,
  DropdownMultiSelect: DropdownConditions,
  LookUp: DropdownConditions,
} as const;

export type GogovObject<T extends Record<string, FieldValue>> = {
  id: number;
  cityId: number;
  defaultFields: T;
  customFields?: Record<string, FieldValue>;
};

export type FieldValue = ListFieldDefinition & {
  fieldId?: number;
  stringValue?: string;
  integerValue?: number;
  floatValue?: number;
  dateValue?: string;
  booleanValue?: boolean;
  jsonValue?: Object;
  addressValue?: {
    attn?: string;
    zip: string;
    address: string;
    address2: string;
    city: string;
    rowTimestamp: string;
    id?: number;
    state: string;
    full: string;
  };
  lookUpValue?: GogovObject<Record<string, FieldValue>>;
  lookUpManyValue?: GogovObject<Record<string, FieldValue>>[];
};

//convert the commented out code into a similar discriminated union type except where the discriminator is `type`
export type FormDataFilter =
  | {
      type: "Text" | "TextArea" | "Email" | "URL" | "Address" | "PhoneNumber";
      field: string;
      fieldId?: number | null;
      fieldTable?: string;
      label: string;
      filter: {
        jsonValue: string[];
        stringCondition: StringCondition;
      };
    }
  | {
      type: "LookUp";
      field: string;
      fieldId?: number | null;
      fieldTable?: string;
      label: string;
      lookUpType: ObjectType;
      lookUpNeedsSearch?: boolean;
      filter: {
        jsonValue: (string | number)[];
        dropdownCondition: DropdownCondition;
      };
    }
  | {
      type: "Dropdown" | "DropdownMultiSelect";
      field: string;
      fieldId?: number | null;
      fieldTable?: string;
      label: string;
      picklist?: {
        key: string;
        value: string;
        text: string | JSX.Element;
        header?: boolean;
        disabled?: boolean;
        className?: string;
      }[];
      filter: {
        jsonValue: (string | number)[];
        dropdownCondition: DropdownCondition;
      };
    }
  | {
      type: "Number" | "Currency" | "Percent";
      field: string;
      fieldId?: number | null;
      fieldTable?: string;
      label: string;
      filter: {
        numberValue: {
          firstNumber?: number;
          secondNumber?: number;
        };
        numberCondition: NumberCondition;
      };
    }
  | {
      type: "Checkbox";
      field: string;
      fieldId?: number | null;
      fieldTable?: string;
      label: string;
      filter: {
        booleanValue: boolean;
        booleanCondition: BooleanCondition;
      };
    }
  | {
      type: "Date" | "DateTime" | "Time";
      field: string;
      fieldId?: number | null;
      fieldTable?: string;
      label: string;
      filter: {
        dateValue: {
          startDate?: Date;
          endDate?: Date;
          dateOption?: string;
        };
        dateCondition: DateCondition;
      };
    };

export type FormPicklist = Exclude<
  Extract<FormDataFilter, { type: "Dropdown" | "DropdownMultiSelect" }>["picklist"],
  undefined
>;
